import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { RefreshCw, ShieldAlert, ShieldCheck, UserX, UserCheck, Clock, Filter, Eye, DollarSign, Gavel, Star, Lock, KeyRound, Settings, UserCog, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../App';

const AdminAuditLogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState('all');
  const [limit, setLimit] = useState(50);
  const [expandedId, setExpandedId] = useState(null);

  const fetchAuditLogs = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      let url = `${API}/admin/audit-logs?limit=${limit}`;
      if (actionFilter !== 'all') {
        url += `&action_type=${actionFilter}`;
      }
      
      const response = await axios.get(url, { headers });
      setLogs(response.data);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [actionFilter, limit]);

  useEffect(() => {
    fetchAuditLogs();
  }, [fetchAuditLogs]);

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'vendor_blocked':
        return <ShieldAlert className="h-4 w-4 text-red-500" />;
      case 'vendor_unblocked':
        return <ShieldCheck className="h-4 w-4 text-green-500" />;
      case 'user_suspended':
        return <UserX className="h-4 w-4 text-red-500" />;
      case 'user_unsuspended':
        return <UserCheck className="h-4 w-4 text-green-500" />;
      case 'impersonation_start':
        return <Eye className="h-4 w-4 text-purple-500" />;
      case 'impersonation_exit':
        return <Eye className="h-4 w-4 text-gray-400" />;
      case 'impersonation_action':
        return <UserCog className="h-4 w-4 text-purple-600" />;
      case 'fee_waiver_applied':
      case 'fee_waiver_extended':
        return <DollarSign className="h-4 w-4 text-amber-500" />;
      case 'fee_waiver_ended':
        return <DollarSign className="h-4 w-4 text-red-500" />;
      case 'force_accept_proposal':
        return <Gavel className="h-4 w-4 text-orange-500" />;
      case 'dispute_resolved':
        return <Gavel className="h-4 w-4 text-blue-500" />;
      case 'review_moderated':
      case 'review_deleted':
        return <Star className="h-4 w-4 text-amber-500" />;
      case 'account_unlocked':
        return <Lock className="h-4 w-4 text-green-500" />;
      case 'password_reset':
        return <KeyRound className="h-4 w-4 text-red-500" />;
      case 'platform_settings_changed':
        return <Settings className="h-4 w-4 text-blue-500" />;
      case 'grant_referral_reward':
        return <DollarSign className="h-4 w-4 text-green-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getActionBadge = (actionType) => {
    const badgeStyles = {
      vendor_blocked: 'bg-red-100 text-red-800',
      vendor_unblocked: 'bg-green-100 text-green-800',
      user_suspended: 'bg-orange-100 text-orange-800',
      user_unsuspended: 'bg-blue-100 text-blue-800',
      impersonation_start: 'bg-purple-100 text-purple-800',
      impersonation_exit: 'bg-gray-100 text-gray-700',
      impersonation_action: 'bg-purple-100 text-purple-800',
      fee_waiver_applied: 'bg-amber-100 text-amber-800',
      fee_waiver_ended: 'bg-red-100 text-red-800',
      fee_waiver_extended: 'bg-amber-100 text-amber-800',
      force_accept_proposal: 'bg-orange-100 text-orange-800',
      dispute_resolved: 'bg-blue-100 text-blue-800',
      review_moderated: 'bg-yellow-100 text-yellow-800',
      review_deleted: 'bg-red-100 text-red-800',
      account_unlocked: 'bg-green-100 text-green-800',
      password_reset: 'bg-red-100 text-red-800',
      platform_settings_changed: 'bg-blue-100 text-blue-800',
      grant_referral_reward: 'bg-green-100 text-green-800',
    };
    
    const labels = {
      vendor_blocked: 'Vendor Blocked',
      vendor_unblocked: 'Vendor Unblocked',
      user_suspended: 'User Suspended',
      user_unsuspended: 'User Unsuspended',
      impersonation_start: 'Impersonation Start',
      impersonation_exit: 'Impersonation Exit',
      impersonation_action: 'Action While Impersonating',
      fee_waiver_applied: 'Fee Waiver Applied',
      fee_waiver_ended: 'Fee Waiver Ended',
      fee_waiver_extended: 'Fee Waiver Extended',
      force_accept_proposal: 'Force Accept',
      dispute_resolved: 'Dispute Updated',
      review_moderated: 'Review Moderated',
      review_deleted: 'Review Deleted',
      account_unlocked: 'Account Unlocked',
      password_reset: 'Password Reset',
      platform_settings_changed: 'Settings Changed',
      grant_referral_reward: 'Referral Reward',
    };
    
    return (
      <Badge className={badgeStyles[actionType] || 'bg-gray-100 text-gray-800'}>
        {labels[actionType] || actionType}
      </Badge>
    );
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getTargetName = (log) => {
    if (log.target_type === 'vendor') {
      return log.details?.vendor_name || log.target_id;
    }
    if (log.target_type === 'user') {
      return log.details?.user_email || log.details?.user_name || log.details?.impersonated_email || log.details?.unlocked_email || log.details?.reset_email || log.target_id;
    }
    if (log.target_type === 'booking') {
      return log.details?.proposal_id ? `Proposal → Booking` : log.target_id;
    }
    if (log.target_type === 'dispute') {
      return `Dispute ${log.target_id?.substring(0, 8)}...`;
    }
    if (log.target_type === 'review') {
      return `Review ${log.target_id?.substring(0, 8)}...`;
    }
    if (log.target_type === 'system') {
      return 'Platform Settings';
    }
    return log.target_id;
  };

  const getQuickSummary = (log) => {
    const d = log.details || {};
    switch (log.action_type) {
      case 'impersonation_action':
        return d.action_label || `${d.method} ${d.endpoint?.replace('/api/', '/')}`;
      case 'impersonation_start':
      case 'impersonation_exit':
        return `${d.impersonated_email}${d.impersonated_role ? ` (${d.impersonated_role})` : ''}`;
      case 'user_suspended':
        return d.suspension_reason || 'Suspended';
      case 'vendor_blocked':
      case 'vendor_unblocked':
        return `${d.previous_admin_visible ? 'Visible' : 'Hidden'} → ${d.new_admin_visible ? 'Visible' : 'Hidden'}`;
      case 'fee_waiver_applied':
        return `${d.fee_percent}% fee · ${d.duration_days} days`;
      case 'fee_waiver_ended':
        return `Was ${d.previous_fee_percent}% → Default`;
      case 'fee_waiver_extended':
        return `+${d.additional_days} days → ${d.new_end_at}`;
      case 'force_accept_proposal':
        return `$${d.agreed_price} · ${d.override_reason}`;
      case 'dispute_resolved':
        return `→ ${d.new_status}`;
      case 'review_moderated':
      case 'review_deleted':
        return `${d.is_approved ? 'Approved' : 'Hidden'}${d.comment_edited ? ' · edited' : ''} · ★${d.rating}`;
      case 'account_unlocked':
      case 'password_reset':
        return d.unlocked_email || d.reset_email;
      case 'platform_settings_changed':
        if (d.changes) {
          return Object.entries(d.changes).map(([k, v]) => `${k.replace(/_/g, ' ')}: ${v.from} → ${v.to}`).join(', ');
        }
        return 'Settings updated';
      case 'grant_referral_reward':
        return `${d.rewarded_referral_email} · Month ${d.total_months_granted}`;
      default:
        return '';
    }
  };

  const hasExpandableDetails = (log) => {
    if (log.action_type === 'impersonation_action' && log.details?.changes && Object.keys(log.details.changes).length > 0) return true;
    if (log.action_type === 'platform_settings_changed' && log.details?.changes) return true;
    if (log.action_type === 'force_accept_proposal') return true;
    if (log.action_type === 'fee_waiver_applied') return true;
    return false;
  };

  const renderExpandedDetails = (log) => {
    const d = log.details || {};
    
    if (log.action_type === 'impersonation_action' && d.changes && Object.keys(d.changes).length > 0) {
      // Check if changes have from/to format (diff) or just values
      const isDiff = Object.values(d.changes).some(v => v && typeof v === 'object' && 'from' in v);
      
      return (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Fields Changed</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {Object.entries(d.changes).map(([key, value]) => (
              <div key={key} className="bg-gray-50 rounded-lg p-2.5 border border-gray-100">
                <p className="text-xs font-medium text-gray-500 mb-1">{key.replace(/_/g, ' ')}</p>
                {isDiff && value && typeof value === 'object' && 'from' in value ? (
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm text-red-500 line-through break-all">{String(value.from ?? '(empty)')}</span>
                    <span className="text-gray-400">→</span>
                    <span className="text-sm text-green-600 font-medium break-all">{String(value.to ?? '(empty)')}</span>
                  </div>
                ) : (
                  <p className="text-sm text-gray-900 break-all">
                    {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                  </p>
                )}
              </div>
            ))}
          </div>
          <div className="flex gap-4 text-xs text-gray-400 pt-1">
            <span>{d.method} {d.endpoint}</span>
            <span>Status: {d.status_code}</span>
          </div>
        </div>
      );
    }
    
    if (log.action_type === 'platform_settings_changed' && d.changes) {
      return (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Settings Changed</p>
          {Object.entries(d.changes).map(([key, val]) => (
            <div key={key} className="flex items-center gap-2 bg-gray-50 rounded-lg p-2.5 border border-gray-100">
              <span className="text-xs font-medium text-gray-500 min-w-[120px]">{key.replace(/_/g, ' ')}</span>
              <span className="text-sm text-red-500 line-through">{String(val.from)}</span>
              <span className="text-gray-400">→</span>
              <span className="text-sm text-green-600 font-medium">{String(val.to)}</span>
            </div>
          ))}
        </div>
      );
    }
    
    if (log.action_type === 'force_accept_proposal') {
      return (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Override Details</p>
          <div className="grid grid-cols-2 gap-2">
            <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-100">
              <p className="text-xs text-gray-500">Agreed Price</p>
              <p className="text-sm font-medium text-gray-900">${d.agreed_price}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-100">
              <p className="text-xs text-gray-500">Override Reason</p>
              <p className="text-sm text-gray-900">{d.override_reason}</p>
            </div>
            {d.original_blocked_reason && (
              <div className="col-span-2 bg-red-50 rounded-lg p-2.5 border border-red-100">
                <p className="text-xs text-red-500">Bypassed Restriction</p>
                <p className="text-sm text-red-700">{d.original_blocked_reason}</p>
              </div>
            )}
          </div>
        </div>
      );
    }
    
    if (log.action_type === 'fee_waiver_applied') {
      return (
        <div className="space-y-2">
          <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Fee Waiver Details</p>
          <div className="grid grid-cols-3 gap-2">
            <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-100">
              <p className="text-xs text-gray-500">New Fee Rate</p>
              <p className="text-sm font-medium text-gray-900">{d.fee_percent}%</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-100">
              <p className="text-xs text-gray-500">Duration</p>
              <p className="text-sm text-gray-900">{d.duration_days} days</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-100">
              <p className="text-xs text-gray-500">Expires</p>
              <p className="text-sm text-gray-900">{d.end_at}</p>
            </div>
          </div>
          {d.reason && (
            <div className="bg-gray-50 rounded-lg p-2.5 border border-gray-100">
              <p className="text-xs text-gray-500">Reason</p>
              <p className="text-sm text-gray-900">{d.reason}</p>
            </div>
          )}
        </div>
      );
    }
    
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl">Audit Logs</CardTitle>
            <CardDescription>Track admin actions for accountability and troubleshooting</CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={fetchAuditLogs}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-500" />
            <span className="text-sm text-gray-600">Filter by:</span>
          </div>
          <Select value={actionFilter} onValueChange={setActionFilter}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="All Actions" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Actions</SelectItem>
              <SelectItem value="impersonation_start">Impersonation Start</SelectItem>
              <SelectItem value="impersonation_exit">Impersonation Exit</SelectItem>
              <SelectItem value="impersonation_action">Action While Impersonating</SelectItem>
              <SelectItem value="vendor_blocked">Vendor Blocked</SelectItem>
              <SelectItem value="vendor_unblocked">Vendor Unblocked</SelectItem>
              <SelectItem value="user_suspended">User Suspended</SelectItem>
              <SelectItem value="user_unsuspended">User Unsuspended</SelectItem>
              <SelectItem value="fee_waiver_applied">Fee Waiver Applied</SelectItem>
              <SelectItem value="fee_waiver_ended">Fee Waiver Ended</SelectItem>
              <SelectItem value="fee_waiver_extended">Fee Waiver Extended</SelectItem>
              <SelectItem value="force_accept_proposal">Force Accept Proposal</SelectItem>
              <SelectItem value="dispute_resolved">Dispute Updated</SelectItem>
              <SelectItem value="review_moderated">Review Moderated</SelectItem>
              <SelectItem value="review_deleted">Review Deleted</SelectItem>
              <SelectItem value="account_unlocked">Account Unlocked</SelectItem>
              <SelectItem value="password_reset">Password Reset</SelectItem>
              <SelectItem value="platform_settings_changed">Settings Changed</SelectItem>
              <SelectItem value="grant_referral_reward">Referral Reward</SelectItem>
            </SelectContent>
          </Select>
          <Select value={limit.toString()} onValueChange={(v) => setLimit(parseInt(v))}>
            <SelectTrigger className="w-[120px]">
              <SelectValue placeholder="Limit" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="25">Last 25</SelectItem>
              <SelectItem value="50">Last 50</SelectItem>
              <SelectItem value="100">Last 100</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Logs Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="h-6 w-6 animate-spin text-gray-400" />
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-12">
            <Clock className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No audit logs found</p>
            <p className="text-sm text-gray-400 mt-1">
              Admin actions will appear here once they occur
            </p>
          </div>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="w-[50px]"></TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Admin</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead className="text-right">Date & Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {logs.map((log) => {
                  const expandable = hasExpandableDetails(log);
                  const isExpanded = expandedId === log.id;
                  return (
                    <React.Fragment key={log.id}>
                      <TableRow 
                        className={`${expandable ? 'cursor-pointer' : ''} hover:bg-gray-50 ${isExpanded ? 'bg-gray-50 border-b-0' : ''}`}
                        onClick={() => expandable && setExpandedId(isExpanded ? null : log.id)}
                        data-testid={`audit-row-${log.id}`}
                      >
                        <TableCell className="w-[30px]">
                          {expandable ? (
                            isExpanded ? <ChevronDown className="h-4 w-4 text-gray-400" /> : <ChevronRight className="h-4 w-4 text-gray-400" />
                          ) : (
                            getActionIcon(log.action_type)
                          )}
                        </TableCell>
                        <TableCell>{getActionBadge(log.action_type)}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium text-gray-900">{getTargetName(log)}</p>
                            <p className="text-xs text-gray-500 capitalize">{log.target_type}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <p className="text-sm text-gray-700">{log.admin_email}</p>
                        </TableCell>
                        <TableCell>
                          <p className="text-xs text-gray-500 max-w-[280px] truncate">
                            {getQuickSummary(log)}
                          </p>
                        </TableCell>
                        <TableCell className="text-right">
                          <p className="text-sm text-gray-600">{formatDate(log.created_at)}</p>
                        </TableCell>
                      </TableRow>
                      {isExpanded && (
                        <TableRow className="bg-gray-50/80">
                          <TableCell colSpan={6} className="py-4 px-6">
                            {renderExpandedDetails(log)}
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}

        {/* Summary */}
        {logs.length > 0 && (
          <div className="mt-4 text-sm text-gray-500 text-right">
            Showing {logs.length} log{logs.length !== 1 ? 's' : ''}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminAuditLogsTab;
